import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const CategoriesContext = createContext();

export function useCategories() {
  return useContext(CategoriesContext);
}

export function CategoriesProvider({ children }) {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);
  // Add a flag to track initialization state
  const isInitializedRef = useRef(false);
  // Add activity tracking
  const lastActivityRef = useRef(Date.now());
  const checkActivityIntervalRef = useRef(null);

  // Function to check if the app is in active use
  const isActivelyUsed = useCallback(() => {
    const now = Date.now();
    const INACTIVITY_THRESHOLD = 10 * 60 * 1000; // 10 minutes
    return (now - lastActivityRef.current) < INACTIVITY_THRESHOLD;
  }, []);

  // Function to update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Memoize the categories processing function - moved up to fix reference error
  const processCategories = useCallback((data) => {
    if (!data?.groups) return [];
    return data.groups
      .map(group => ({
        ...group,
        items: group.items.filter(item => !item.isArchived)
      }))
      .filter(group => group.items.length > 0);
  }, []);

  // Set up event listeners to track user activity
  useEffect(() => {
    // Skip if no user
    if (!user) return;
    
    // Register user activity events
    const activityEvents = ['mousedown', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      updateActivity();
    };
    
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [updateActivity, user]);

  // Function to toggle listener based on activity
  const manageListener = useCallback((isActive) => {
    if (!user) return;
    
    if (!isActive && unsubscribeRef.current) {
      console.log('CategoriesContext: Suspending real-time listener due to inactivity');
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    } else if (isActive && !unsubscribeRef.current && categories.length === 0) {
      console.log('CategoriesContext: Resuming listener due to user activity');
      // Reset the initialization flag to force a new fetch
      isInitializedRef.current = false;
      
      // Setup listener again
      let isMounted = true;
      const setupListener = () => {
        try {
          console.log('Setting up categories listener');
          unsubscribeRef.current = onSnapshot(
            doc(db, 'categories', 'active_categories'),
            { includeMetadataChanges: false },
            (doc) => {
              if (!isMounted) return;
  
              if (doc.exists()) {
                const data = doc.data();
                const activeCategories = processCategories(data);
                setCategories(activeCategories);
              } else {
                setCategories([]);
              }
              setLoading(false);
              isInitializedRef.current = true;
              updateActivity();
            },
            (error) => {
              if (!isMounted) return;
              console.error('Error loading categories:', error);
              setError(error.message);
              setLoading(false);
            }
          );
        } catch (error) {
          if (!isMounted) return;
          console.error('Error setting up categories listener:', error);
          setError(error.message);
          setLoading(false);
        }
      };
      
      setupListener();
    }
  }, [categories.length, processCategories, updateActivity, user]);

  // Set up periodic check for inactivity
  useEffect(() => {
    if (!user) return;
    
    checkActivityIntervalRef.current = setInterval(() => {
      manageListener(isActivelyUsed());
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => {
      if (checkActivityIntervalRef.current) {
        clearInterval(checkActivityIntervalRef.current);
        checkActivityIntervalRef.current = null;
      }
    };
  }, [isActivelyUsed, manageListener, user]);

  useEffect(() => {
    // If no user, clear categories and exit
    if (!user) {
      setCategories([]);
      setLoading(false);
      return;
    }

    // Skip setup if we already have data and the listener is initialized
    if (isInitializedRef.current && categories.length > 0 && unsubscribeRef.current) {
      return;
    }
    
    // Update activity when setting up a listener
    updateActivity();

    let isMounted = true;

    const setupListener = () => {
      try {
        // Clean up any existing listener first
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }

        console.log('Setting up categories listener');
        unsubscribeRef.current = onSnapshot(
          doc(db, 'categories', 'active_categories'),
          { includeMetadataChanges: false },
          (doc) => {
            if (!isMounted) return;

            if (doc.exists()) {
              const data = doc.data();
              const activeCategories = processCategories(data);
              setCategories(activeCategories);
            } else {
              setCategories([]);
            }
            setLoading(false);
            isInitializedRef.current = true;
            updateActivity();
          },
          (error) => {
            if (!isMounted) return;
            console.error('Error loading categories:', error);
            setError(error.message);
            setLoading(false);
          }
        );
      } catch (error) {
        if (!isMounted) return;
        console.error('Error setting up categories listener:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    setupListener();

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        console.log('CategoriesContext: Unsubscribing listener');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (checkActivityIntervalRef.current) {
        clearInterval(checkActivityIntervalRef.current);
        checkActivityIntervalRef.current = null;
      }
    };
  }, [user, processCategories, updateActivity, categories.length]); // Added categories.length dependency

  // Memoize helper functions
  const getFieldByName = useCallback((fieldName) => {
    for (const group of categories) {
      const item = group.items.find(item => item.fieldName === fieldName);
      if (item) return item;
    }
    return null;
  }, [categories]);

  const getCategoryGroups = useCallback(() => categories, [categories]);

  const getActiveFields = useCallback(() => 
    categories.flatMap(group => group.items.filter(item => !item.isArchived)),
    [categories]
  );

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    categories,
    loading,
    error,
    getFieldByName,
    getCategoryGroups,
    getActiveFields
  }), [categories, loading, error, getFieldByName, getCategoryGroups, getActiveFields]);

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  );
} 